/**
 * A member's long-term holdings seen through their funds: what a fund holds, as its house
 * discloses it each month, and what a member holds of it. Browser-safe: the console names these.
 */

/** What a line of a fund, or a member's own holding, is in the end. */
export const ASSETS = [
  'equity',
  'foreign',
  'debt',
  'cash',
  'gold',
  'silver',
  'reit',
  'fund',
  'other',
] as const;
export type Asset = (typeof ASSETS)[number];

/**
 * By rank in AMFI's list of six months' average market cap: SEBI's large (the top 100) and mid
 * (the next 150), with its small split where NSE's indices split it, at 500: past it is micro.
 */
export const SIZES = ['large', 'mid', 'small', 'micro'] as const;
export type Size = (typeof SIZES)[number];

export const sizeOfRank = (rank: number): Size =>
  rank <= 100 ? 'large' : rank <= 250 ? 'mid' : rank <= 500 ? 'small' : 'micro';

/**
 * A debt line's credit, by its rating and its ISIN: the sovereign's own paper, then by its rating,
 * a short-term A1+ with AAA. `unknown` is debt no line says the credit of: a fund counted by its
 * category, a bond held directly.
 */
export const CREDITS = ['sovereign', 'aaa', 'aa', 'below', 'unrated', 'unknown'] as const;
export type Credit = (typeof CREDITS)[number];

/** An expense ratio's parts, as AMFI publishes them from April 2026; `levies` is GST and the rest. */
export interface CostParts {
  base: number;
  brokerage: number;
  transaction: number;
  levies: number;
}

export interface PortfolioFacts {
  yield?: number;
  maturityDays?: number;
}

export interface DisclosedLine {
  name: string;
  /** The security's own; a future's is its underlying stock's once matched, else null. */
  isin: string | null;
  /** AMFI's industry for a stock; a bond's rating. */
  industry: string | null;
  /** A bond's rating, where its sheet gives it a column apart from the industry's. */
  rating?: string;
  asset: Asset;
  /** Of net assets, as a fraction; a short future's is negative. */
  weight: number;
  /**
   * The shares or units held, where the sheet says: a weight moves with prices, so only a change
   * in this says what a manager bought or sold; a short future's is negative. Absent from
   * portfolios read before it was kept, and from futures read before theirs was.
   */
  quantity?: number;
  /** Yield to maturity. */
  yield?: number;
  /** A future or an option rather than the security itself. */
  derivative?: 'future' | 'option';
  /** A future on an index, which no one stock stands for. */
  index?: true;
}

/** One of a member's demat lines as their Kite gave it at the close: a stock, an ETF, a bond, gold. */
export interface HeldPaper {
  isin: string | null;
  symbol: string;
  quantity: number;
  averagePrice: number;
  lastPrice: number;
  closePrice: number;
}

export const holdsFunds = (held: {
  papers: readonly HeldPaper[];
  funds: readonly HeldFund[];
}): boolean => held.funds.length > 0 || held.papers.some((paper) => paper.isin?.startsWith('INF'));

/** One of a member's Coin funds: `isin` is Kite's tradingsymbol for it. */
export interface HeldFund {
  isin: string;
  name: string | null;
  units: number;
  averageNav: number;
  nav: number;
}
